import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private _unreadCount = signal(0);
  readonly unreadCount = this._unreadCount.asReadonly();

  constructor(private http: HttpClient) {}

  public refreshUnreadCount(): void {
    this.getUnreadCount().subscribe({
      next: (resp: any) => this._unreadCount.set(resp.record || 0),
    });
  }

  public adjustUnreadCount(delta: number): void {
    this._unreadCount.update((count) => Math.max(0, count + delta));
  }

  public clearUnreadCount(): void {
    this._unreadCount.set(0);
  }

  public list(filter: any = null, limit: number = 0, page: number = 1) {
    const path = 'api/notifications/list';
    const params: any = {};
    params.filter = filter ? JSON.stringify(filter) : '{}';
    params.limit = limit;
    params.page = page;
    return this.http.get(path, { params });
  }

  public getList(filter: any = {}, page: number = 0, limit: number = 0) {
    const path = 'api/notifications';
    const params: any = {};
    if (filter.where) {
      params.where = JSON.stringify(filter.where);
    }
    if (filter.columns) {
      params.columns = JSON.stringify(filter.columns);
    }
    if (filter.include) {
      params.include = JSON.stringify(filter.include);
    }
    if (filter.orderBy) {
      params.order = JSON.stringify(filter.orderBy);
    }
    if (page) {
      params.page = page;
    }
    if (limit) {
      params.limit = limit;
    }
    return this.http.get(path, { params });
  }

  public setIsRead(id: number, isRead: boolean = true) {
    const path = `api/notifications/${id}`;
    return this.http.put(path, { is_read: isRead });
  }

  public markIsRead(id: number) {
    return this.setIsRead(id, true);
  }

  public markAllIsRead() {
    const path = `api/notifications/all`;
    return this.http.post(path, {});
  }

  public getUnreadCount() {
    const path = `api/notifications/unread-count`;
    return this.http.get(path);
  }

  public route(value: any, _default: any = ['/notifications', { queryParams: { _ref: '/notifications' } }]): any {
    switch (value.object_type) {
      case 'user':
        return [`/profile`, { queryParams: { _ref: '/notifications' } }];
      case 'application':
        return [`/eservices/list`, { queryParams: { _ref: '/notifications', application_id: value.object_id } }];
      case 'job':
        if (value.activity == 'Job Completion Request' || value.activity == 'Job Completed' || value.activity == 'Job accepted' || value.activity == 'Job Review') {
          return [`/procurement/work-orders/detail/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
        } else {
          return [`/procurement/rfps/detail/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
        }
      case 'association':
        return [`/associations/details/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
      case 'organization':
        return [`/organizations/details`, { queryParams: { _ref: '/notifications' } }];
      case 'receipt':
        return [`/accounting/receipts`, { queryParams: { _ref: '/notifications' } }];
      case 'invoice':
        return [`/accounting/invoices`, { queryParams: { _ref: '/notifications' } }];
      case 'vendor':
        return [`directory/vendors`, { queryParams: { _ref: '/notifications' } }];
      case 'owner':
        return [`directory/owners`, { queryParams: { _ref: '/notifications' } }];
      case 'representative':
        return [`directory/agents`, { queryParams: { _ref: '/notifications' } }];
      case 'job_application':
        return [`/procurement/rfps/detail/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
      case 'bill':
        return [`accounting/bills`, { queryParams: { _ref: '/notifications' } }];
      case 'unit':
        return [`associations/units/details/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
      case 'notice':
        return [`/noticeboard/view/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
      case 'task':
        return [`/tasks/detail/${value.object_id}`, { queryParams: { _ref: '/notifications' } }];
      case 'complain_suggestion':
        return [`/complaints-suggestions/list`, { queryParams: { _ref: '/notifications', id: value.object_id } }];
      case 'payment_receipt':
        return [`/accounting/receipts/list`, { queryParams: { _ref: '/notifications' } }];
      case 'ownership_changed':
        if (value.object_id) {
          return [`associations/units/details/${value.object_id}`, { queryParams: { _ref: '/notifications', pages: 'Owner' } }];
        } else {
          return _default;
        }
      case 'budget_approved':
        return [`/accounting/budgets/external`, { queryParams: { _ref: '/notifications', associationId: value.object_id } }];
      case 'invoice_generated':
        return [`/accounting/invoices`, { queryParams: { _ref: '/notifications' } }];
      case 'email_enquiry':
        return [`/happiness-center/online`, { queryParams: { _ref: '/notifications', happiness_center_id: value.object_id, type: value.activity } }];
      case 'jp_chat':
        return [`/procurement/messages`, { queryParams: { _ref: '/notifications', chat_id: value.object_id } }];
      case 'petty_cash':
        return [`/accounting/petty-cash`, { queryParams: { _ref: '/notifications', tab: 'history' } }];
      default:
        return _default;
    }
  }

  public vendorRoute(value: any, _default: any = ['/vendor/notifications', { queryParams: { _ref: '/vendor/notifications' } }]): any {
    switch (value.object_type) {
      case 'user':
        return [`/vendor/profile`, { queryParams: { _ref: '/vendor/notifications' } }];
      case 'job':
        if (
          value.activity == 'Job Incomplete' ||
          value.activity == 'Job Review' ||
          value.activity == 'Job Completed' ||
          value.activity == 'Work Order Issued' ||
          value.activity == 'Job Terminated' ||
          value.activity == 'Invoice Returned' ||
          value.activity == 'Credit Note Returned' ||
          value.activity == 'Job Contact Person' ||
          value.activity == 'Active Job Message'
        ) {
          return [`/vendor/jobs/active/details/${value.object_id}`, { queryParams: { _ref: '/vendor/notifications' } }];
        } else if (value.activity == 'Applied Job Message') {
          return [`/vendor/jobs/applied`, { queryParams: { _ref: '/vendor/notifications', id: value.object_id } }];
        } else {
          return [`/vendor/jobs/details/${value.object_id}`, { queryParams: { _ref: '/vendor/notifications' } }];
        }
      case 'organization':
        return [`/vendor/organization/details`, { queryParams: { _ref: '/vendor/notifications' } }];
      case 'vendor':
        return [`/vendor/organization/details`, { queryParams: { _ref: '/vendor/notifications' } }];
      case 'complain_suggestion':
        return [`/vendor/complaints-suggestion`, { queryParams: { _ref: '/vendor/notifications' } }];
      case 'job_application':
        return [`/vendor/jobs/details/${value.object_id}`, { queryParams: { _ref: '/vendor/notifications' } }];
      case 'wallet':
        return [`/vendor/ewallet`, { queryParams: { _ref: '/vendor/notifications' } }];
      case 'email_enquiry':
        return [`/vendor/complaints-suggestions`, { queryParams: { _ref: '/vendor/notifications', complaint_id: value.object_id, type: value.activity } }];
      default:
        return _default;
    }
  }

  public link(item: any, companyType: string): { link: string; linkParams: any } {
    const route = companyType === 'oam' ? this.route(item) : this.vendorRoute(item);
    const path: string = route?.[0] || '/notifications';

    return {
      link: ('/' + path).replace(/\/+/g, '/').replace(/\/+$/, ''),
      linkParams: route?.[1]?.queryParams || {},
    };
  }

  public getGroups() {
    return [
      {
        label: 'Accounting',
        items: [{ label: 'Bill', value: 'Bill' }],
      },
      {
        label: 'Job',
        items: [
          { label: 'New Job', value: 'New Job' },
          { label: 'Job Updated', value: 'Job Updated' },
          { label: 'Job Cancelled', value: 'Job Cancelled' },
          { label: 'Job Completed', value: 'Job Completed' },
          { label: 'Job Completion Request', value: 'Job Completion Request' },
          { label: 'Job Contact Person', value: 'Job Contact Person' },
          { label: 'Job Message', value: 'Job Message' },
          { label: 'Job Review', value: 'Job Review' },
          { label: 'Job Terminated', value: 'Job Terminated' },
          { label: 'Job Accepted', value: 'Job Accepted' },
          { label: 'Job Declined', value: 'Job Declined' },
        ],
      },
      {
        label: 'Task',
        items: [
          { label: 'New Task', value: 'New Task' },
          { label: 'New Updated', value: 'New Updated' },
        ],
      },
      {
        label: 'E-Service',
        items: [{ label: 'New Request', value: 'New Request' }],
      },
    ];
  }
}
